import { useCallback } from 'react';

import { IUseAddUserToTeamReturn } from './useAddUserToTeam.decl';

import { SUCCESS_FEEDBACK_MESSAGES } from '@constants/translation.constants';
import { getFullName } from '@dashboard/library';
import { TeamFragment } from '@generated/TeamFragment';
import { UpdateTeamMutation, UpdateTeamMutationVariables } from '@generated/UpdateTeamMutation';
import { UserFullFragment } from '@generated/UserFullFragment';
import { UserMinimalFragment } from '@generated/UserMinimalFragment';
import { TEAM_FRAGMENT } from '@graphql/fragments/TeamFragment';
import { UPDATE_TEAM_MUTATION } from '@graphql/mutations/UpdateTeamMutation';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { useTracker } from '@hooks/useTracker/useTracker';
import { useTranslation } from 'react-i18next';

export function useAddUserToTeam(): IUseAddUserToTeamReturn {
  const { t } = useTranslation();
  const { track } = useTracker();

  const [mutate] = useGraphMutationWithFeedback<UpdateTeamMutation, UpdateTeamMutationVariables>(
    UPDATE_TEAM_MUTATION,
    { type: 'snackbar' },
    { onCompleted: () => track({ event: 'user_added_to_a_team' }) }
  );

  const addUserToTeam = useCallback(
    async (team: TeamFragment, user: UserMinimalFragment | UserFullFragment) => {
      const userId = parseInt(user.ID);
      const updatedTeam = {
        ...team,
        agentsCounter: (team.agentsCounter ?? 0) + 1,
        agentsIds: [...(team.agentsIds ?? []), userId],
      };

      const { error } = await mutate(
        {
          variables: {
            input: {
              ID: team.ID,
              agentsIdsToAdd: [userId],
            },
          },
          update(cache) {
            cache.modify({
              id: cache.identify({ __typename: 'AgentV2', ID: user.ID }),
              fields: {
                teams(teamsRef) {
                  const updatedTeamRef = cache.writeFragment({
                    data: updatedTeam,
                    fragment: TEAM_FRAGMENT,
                  });
                  return [...teamsRef, updatedTeamRef];
                },
              },
            });
          },
          optimisticResponse: {
            updateTeam: updatedTeam,
          },
        },
        {
          successMessage: t(SUCCESS_FEEDBACK_MESSAGES.ADD_SUCCESS, {
            name: getFullName(user.firstName, user.lastName),
          }),
        }
      );

      return error;
    },
    [mutate, t]
  );

  return { addUserToTeam };
}
